import {
  Box,
  FormControlLabel,
  IconButton,
  SvgIcon,
  Typography,
} from '@mui/material';
import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
  Button,
  ConfirmationMessageModal,
  FullScreenLoader,
  ToggleSwitch,
} from '@Components/index';
import MainSectionLayout from '@Components/MainSectionLayout';
import commonConstants from '@Constants/common.constants';
import Icons from '@Constants/Icons.constants';
import BuyVonageRotationCallerIdsModal from '@Containers/UserCallerIds/VonageRotationCallerIds/BuyVonageRotationCallerIdsModal';
import { VonageRotationCallerIdsProps } from '@Containers/UserCallerIds/VonageRotationCallerIds/BuyVonageRotationCallerIdsModal/BuyVonageRotationCallerIdsModal.types';
import {
  CallerIdRotationData,
  DeleteRotationCallerIdRequest,
  VoiceProvider,
} from '@Models/callerIds.models';
import { ScriptsQuery } from '@Models/scripts.models';
import {
  useChangeEnablingRotationCallerByIdMutation,
  useDeleteRotationCallerIdMutation,
  useGetRotationCallerIdListQuery,
  useGetTprSettingQuery,
} from '@Services/userCallerIds.service';
import { BaseAlertProps, RotationCallerIdSource } from '@Typings/global.types';
import {
  formatNumber,
  formatToUSNumber,
  getDateMMDDYYYYHHMM,
  getRTKQuery,
} from '@Utils/common/common.utils';
import useAlert from '@Utils/hooks/useAlert.hook';
import { useTokenTprValueHook } from '@Utils/hooks/useTokenValues.hook';

import { StyledStack } from './VonageRotationCallerIds.styles';

const VonageRotationCallerIdsContainer: FunctionComponent<VonageRotationCallerIdsProps> =
  (props: VonageRotationCallerIdsProps): JSX.Element => {
    const { isAdmin } = props;

    const { t } = useTranslation();
    const { triggerAlert } = useAlert();
    const [alertState, setAlertState] = useState<BaseAlertProps>({
      openAlert: false,
    });
    const [
      rotationDeleteConfirmationMessage,
      setRotationDeleteConfirmationMessage,
    ] = useState<React.ReactNode | null>(null);
    const [
      isRotationDeleteConfirmationModalOpen,
      setIsRotationDeleteConfirmationModalOpen,
    ] = useState(false);
    const [
      rotationDeleteConfirmationModalData,
      setRotationDeleteConfirmationModalData,
    ] = useState<DeleteRotationCallerIdRequest>({
      id: '',
      source: RotationCallerIdSource.OWN,
      userId: '',
    });
    const [showVonageRotationModal, setShowVonageRotationModal] =
      useState<boolean>(false);

    const location = useLocation();
    const rotationQuery: ScriptsQuery = useMemo(
      () => getRTKQuery(location.search),
      [location.search]
    );

    const hasTpr = useTokenTprValueHook();
    const isTprDisplayed =
      (isAdmin && rotationQuery.hasUserTpr === 'true') || (!isAdmin && hasTpr);

    // load rotation caller ids list
    const {
      data: dataOfRotationCallerIdList = [],
      isLoading: loadingRotationCallerIdList,
      isFetching: fetchingRotationCallerIdList,
      isError: isErrorForGetRotationCallerIdList,
      error: errorForGetRotationCallerIdList,
    } = useGetRotationCallerIdListQuery({
      userId: rotationQuery.userId?.toString() || '',
      provider: VoiceProvider.VONAGE.toString(),
    });

    // change enabling of rotation callerId
    const [
      changeEnablingRotationCallerById,
      changeEnablingRotationCallerByIdMethods,
    ] = useChangeEnablingRotationCallerByIdMutation();

    // delete rotation callerId
    const [deleteRotationCallerId, deleteRotationCallerIdMethods] =
      useDeleteRotationCallerIdMutation();

    const { data: dataOfTprSettings } = useGetTprSettingQuery({
      id: rotationQuery.userId?.toString() || '',
      provider: VoiceProvider.VONAGE.toString(),
    });

    const availableLimitToBuy = dataOfTprSettings
      ? dataOfTprSettings.availableTprAmount - dataOfTprSettings.usedTprAmount
      : 0;

    /**
     * Deletes rotation callerId
     * @param id Id of the caller
     * @param phoneNumber phone number of the rotation callerId
     * @param source source of the callerId
     */
    const handleDeleteRotationCallerId = (
      id: string,
      phoneNumber: string,
      source: RotationCallerIdSource
    ) => {
      const userId: string = rotationQuery.userId
        ? rotationQuery.userId?.toString()
        : '';
      setRotationDeleteConfirmationModalData({
        id,
        source,
        userId,
      });
      // Formatting phone to US / UA formats
      const phone = formatNumber(phoneNumber);

      const message: React.ReactNode = (
        <Typography
          variant="main"
          sx={{
            '& .phone': {
              color: 'secondary.main',
            },
          }}
        >
          <Trans
            i18nKey="common.confirmationMessages.confirmDelete"
            values={{ phone }}
            components={{
              strong: <strong />,
              span: <span className="phone" />,
            }}
          />
        </Typography>
      );
      setRotationDeleteConfirmationMessage(message);
      setIsRotationDeleteConfirmationModalOpen(true);
    };

    /** Handles toggle enabling state change */
    const handleEnablingRotationCallerIdChange = (id: string | undefined) => {
      if (id) {
        return changeEnablingRotationCallerById({
          id,
          userId: rotationQuery.userId?.toString() || '',
        });
      }
    };

    /**
     * Hides the confirmation modal and deletes the rotation callerId
     * @param data DeleteRotationCallerIdRequest object
     * @returns delete mutation
     */
    const deleteCallerIdActionHandler = (
      data: DeleteRotationCallerIdRequest
    ) => {
      setRotationDeleteConfirmationModalData({
        id: '',
        source: RotationCallerIdSource.OWN,
        userId: '',
      });
      setIsRotationDeleteConfirmationModalOpen(false);
      if (data.id && data.source) {
        return deleteRotationCallerId(data);
      }
    };

    /**
     * Error Alerts for the Get Rotation Caller Ids Query
     */
    useEffect(() => {
      if (
        isErrorForGetRotationCallerIdList &&
        errorForGetRotationCallerIdList
      ) {
        triggerAlert(errorForGetRotationCallerIdList, setAlertState);
      }
    }, [
      triggerAlert,
      errorForGetRotationCallerIdList,
      isErrorForGetRotationCallerIdList,
    ]);

    /** Rotation Caller Ids Table Loading State */
    const loadingRotation =
      loadingRotationCallerIdList || fetchingRotationCallerIdList;

    /**
     * Error and Success alerts for Delete Rotation CallerId
     */
    useEffect(() => {
      if (
        deleteRotationCallerIdMethods.isError &&
        deleteRotationCallerIdMethods.error
      ) {
        triggerAlert(deleteRotationCallerIdMethods.error, setAlertState);
      }
      if (deleteRotationCallerIdMethods.isSuccess) {
        triggerAlert(
          commonConstants.GLOBAL_MESSAGES.ROTATION_NUMBER_DELETE,
          setAlertState
        );
      }
    }, [
      deleteRotationCallerIdMethods.isError,
      deleteRotationCallerIdMethods.isSuccess,
      deleteRotationCallerIdMethods.error,
      deleteRotationCallerIdMethods.data,
      triggerAlert,
    ]);

    /**
     * Error and Success alerts for changing rotation CallerId enabling
     */
    useEffect(() => {
      if (
        changeEnablingRotationCallerByIdMethods.isError &&
        changeEnablingRotationCallerByIdMethods.error
      ) {
        triggerAlert(
          changeEnablingRotationCallerByIdMethods.error,
          setAlertState
        );
      }
    }, [
      changeEnablingRotationCallerByIdMethods.isError,
      changeEnablingRotationCallerByIdMethods.isSuccess,
      changeEnablingRotationCallerByIdMethods.error,
      changeEnablingRotationCallerByIdMethods.data,
      triggerAlert,
    ]);

    /**
     * Constructs the UI (row) of the rotation caller-ids table
     * @param id unique id of the caller
     * @param phone phone number of the caller
     * @param date createdAt date
     * @param isEnabled phone number enabled
     * @param handleDeletingRotationCallerId callback method to delete the rotation caller-id
     * @param handleEnablingRotationCallerId callback method to change the rotation caller-id enabling
     * @returns a single row, populating the data
     */
    const CallerIdRotationTableRow = (
      id: string,
      phone: string,
      date: string,
      isEnabled: boolean,
      handleDeletingRotationCallerId: (
        id: string,
        phone: string,
        source: RotationCallerIdSource
      ) => void,
      handleEnablingRotationCallerId: (id: string) => void
    ): JSX.Element => {
      let deletionSection;

      if (isAdmin) {
        deletionSection = (
          <Box
            display="flex"
            justifyContent="flex-end"
            sx={{ minWidth: '101px' }}
          >
            <IconButton
              onClick={() =>
                handleDeletingRotationCallerId(
                  id,
                  phone,
                  RotationCallerIdSource.TWILIO
                )
              }
              className="delete-icon"
              aria-label="delete"
            >
              <SvgIcon>{Icons.delete}</SvgIcon>
            </IconButton>
          </Box>
        );
      } else {
        deletionSection = <Box sx={{ minWidth: '101px' }} />;
      }

      return (
        <StyledStack key={id}>
          <Typography
            className="phone-number"
            variant="main"
            sx={{ color: isEnabled ? 'text.primary' : 'text.disabled' }}
          >
            {/* eslint-disable-next-line react/destructuring-assignment */}
            {formatToUSNumber(phone)}
          </Typography>
          <Typography
            className="date"
            variant="main"
            sx={{ color: isEnabled ? 'text.primary' : 'text.disabled' }}
          >
            {t('userCallerIds.Created Date', {
              interpolation: {
                escapeValue: false,
              },
              date: getDateMMDDYYYYHHMM(new Date(date), true),
            })}
          </Typography>
          <Typography
            className="number-type"
            variant="main"
            sx={{ color: isEnabled ? 'text.primary' : 'text.disabled' }}
          >
            {t('userCallerIds.System Number')}
          </Typography>
          <Box className="enable-number">
            <FormControlLabel
              control={
                <ToggleSwitch
                  checked={isEnabled}
                  onChange={() => handleEnablingRotationCallerId(id)}
                  color="secondary"
                />
              }
              label={
                <Typography variant="main">
                  {t(
                    isEnabled
                      ? 'common.messages.Enabled'
                      : 'common.messages.Disabled'
                  )}
                </Typography>
              }
            />
          </Box>
          {deletionSection}
        </StyledStack>
      );
    };

    /**
     * Conditionally shows/hides the Add Rotation Number Form
     */
    const AddNumberToRotationForm = (
      <>
        <Typography
          variant="main"
          fontSize="18px"
          fontWeight="bold"
          textAlign="left"
          sx={{ mt: 0.5, mb: 3.2 }}
        >
          {t('userCallerIds.Trusted Phone Head')}
        </Typography>
        <Button
          onClick={() => setShowVonageRotationModal(true)}
          color="secondary"
          variant="outlined"
          disabled={availableLimitToBuy < 1}
          sx={{ minWidth: 12.6 }}
        >
          {t('common.messages.Add Number')}
        </Button>
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          sx={{ width: '100%', backgroundColor: '#F2F5F9', p: 1, my: 1.2 }}
        >
          <SvgIcon>{Icons.infoOutlined}</SvgIcon>
          <Typography variant="main">
            <Trans
              i18nKey="userCallerIds.Available numbers amount"
              components={{ strong: <strong /> }}
              values={{ amount: availableLimitToBuy }}
            />
          </Typography>
        </Box>
      </>
    );

    /**
     * Rotation Caller ids table component
     */
    const CallerIdRotationTableComponent = (
      <>
        <Box sx={{ overflowX: 'auto', minWidth: '100%' }}>
          {dataOfRotationCallerIdList.length > 0 &&
            dataOfRotationCallerIdList.map(
              (item: CallerIdRotationData): JSX.Element =>
                CallerIdRotationTableRow(
                  item.id,
                  item.callerId,
                  item.createdAt,
                  item.isEnabled,
                  handleDeleteRotationCallerId,
                  handleEnablingRotationCallerIdChange
                )
            )}
        </Box>
        <BuyVonageRotationCallerIdsModal
          userId={rotationQuery.userId}
          isOpen={showVonageRotationModal}
          setIsOpen={setShowVonageRotationModal}
          availableLimitToBuy={availableLimitToBuy}
        />
        <ConfirmationMessageModal
          isModalOpen={isRotationDeleteConfirmationModalOpen}
          setIsModalOpen={setIsRotationDeleteConfirmationModalOpen}
          message={rotationDeleteConfirmationMessage}
          onClick={() =>
            deleteCallerIdActionHandler(rotationDeleteConfirmationModalData)
          }
        />
        {dataOfRotationCallerIdList.length === 0 && (
          <Typography
            variant="main"
            fontWeight="bold"
            textAlign="center"
            sx={{ my: 3.2, color: 'action.disabled' }}
          >
            {t('common.table.No data to show')}
          </Typography>
        )}
      </>
    );

    return (
      <>
        {isTprDisplayed && (
          <>
            {loadingRotation ? (
              <FullScreenLoader data-testid="loader" />
            ) : (
              <MainSectionLayout
                alertState={alertState}
                filters={AddNumberToRotationForm}
                table={CallerIdRotationTableComponent}
              />
            )}
          </>
        )}
      </>
    );
  };

export default VonageRotationCallerIdsContainer;
